import { useCallback, useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useQueries } from "react-query";
import classNames from "classnames";

import { CCol, CCollapse, CRow } from "@coreui/react";

import { checkSave, checkUpdated } from "src/apis/purchase_proposal_form.api";
import { ENTITY_GROUP_CODE, PERMISSION_VALUE } from "src/configs/constant";
import { Can } from "src/utils/ability";
import { format } from "src/utils/dayjs";
import { useGetMyStores } from "src/utils/hooks/options";

import { CButton, CDate, CInput, CSelect } from "_components/controls";
import { CActionGroup } from "_components/others";

import CCheckbox from "../../components/Checkbox";

export default ({
  edit,
  isLoading,
  isUpdated,
  setIsUpdated,
  status,
  watch,
  control,
  setValue,
  onStatusChange,
  onAutoFill,
  onAdd,
  onSave,
  onRemove,
  onCorrectQuantity,
}) => {
  //#region Data
  const [autoSuggest, setAutoSuggest] = useState(false);

  const [isSave, setSave] = useState(true);

  const { stores } = useGetMyStores();

  const store_code = useWatch({ control, name: "store_code" });
  const issueDate = useWatch({ control, name: "issueDate" });
  //#endregion

  useEffect(() => {
    if (stores && stores.length > 0 && !edit && !store_code) {
      setValue("storeName", stores[0].label);
      setValue("storeAddress", stores[0].address);
      setValue("storePhone", stores[0].phone);
      setValue("store_code", stores[0].value);
    }
  }, [stores, edit, store_code]);

  //#region Event
  const toggleCollapse = useCallback(() => onStatusChange(1), [onStatusChange]);

  const toggleAutoSuggest = useCallback(
    (v) => {
      onAutoFill(v);
      setAutoSuggest(v);
    },
    [onAutoFill],
  );

  const correct = useCallback(() => onCorrectQuantity(), [onCorrectQuantity]);

  const onClick = useCallback(
    (state) => {
      switch (state) {
        case "add":
          return onAdd();
        case "save":
          return onSave();
        case "remove":
          return onRemove();
      }
    },
    [onAdd, onSave, onRemove],
  );
  //#endregion

  const [{ data: checkUpdatedResponse }, { data: checkSaveResponse }] =
    useQueries([
      {
        queryKey: ["checkUpdated", store_code, issueDate],
        queryFn: () =>
          checkUpdated(store_code, {
            date: format(issueDate, "YYYY-MM-DD"),
          }),
      },
      {
        queryKey: ["checkSave", store_code, issueDate],
        queryFn: () =>
          checkSave(store_code, {
            date: format(issueDate, "YYYY-MM-DD"),
          }),
      },
    ]);

  useEffect(() => {
    if (checkSaveResponse) {
      setSave(checkSaveResponse.data);
    }
  }, [checkSaveResponse]);

  useEffect(() => {
    if (checkUpdatedResponse) {
      setIsUpdated(checkUpdatedResponse.data);
      onAutoFill(autoSuggest);
    }
  }, [checkUpdatedResponse]);

  //#region Render
  return (
    <>
      <CRow>
        <CCol xs="12" className="action">
          <div>
            <CActionGroup
              onClick={onClick}
              canAdd
              canSave={isSave && !isLoading}
              canEdit={false}
              canRemove
            />
          </div>
          {!edit && (
            <div>
              <Can
                do={PERMISSION_VALUE.APPROVE}
                on={ENTITY_GROUP_CODE.PURCHASE_PROPOSAL_FORM}
              >
                <CButton onClick={correct}>Cập nhật định lượng</CButton>
              </Can>
            </div>
          )}
          <div
            className={classNames(
              "btn",
              "btn-primary",
              "btn-collapse",
              "extend",
              status == 1 && "show",
            )}
            onClick={toggleCollapse}
          ></div>
        </CCol>
      </CRow>
      <CCollapse show={status === 1}>
        <CRow className="mt-3">
          <CCol xs="12" sm="12" md="4" lg="3" xl="3" xxl="2">
            <CInput value={watch("code")} disabled label="Số đơn hàng" />
          </CCol>
          <CCol xs="12" sm="12" md="4" lg="3" xl="3" xxl="3">
            <Controller
              name="store_code"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CSelect
                  {...field}
                  label="Chi nhánh"
                  options={stores}
                  select="value"
                  onChangeCallback={(_v, _o) => {
                    setValue("storeName", _o.label);
                    setValue("storeAddress", _o.address);
                    setValue("storePhone", _o.phone);
                  }}
                />
              )}
            />
          </CCol>
          <CCol xs="12" sm="12" md="4" lg="3" xl="2" xxl="2">
            <Controller
              name="issueDate"
              control={control}
              rules={{ required: true }}
              render={({ field }) => <CDate label="Ngày giao" {...field} />}
            />
          </CCol>
          <CCol xs="12" sm="12" md="12" lg="12" xl="4" xxl="5">
            <Controller
              name="reason"
              control={control}
              render={({ field }) => <CInput label="Ghi chú" {...field} />}
            />
          </CCol>
          <CCol xs="12" sm="12" md="6" lg="6" xl="6" xxl="5">
            <CInput
              value={watch("storeAddress") || ""}
              readOnly
              label="Địa chỉ"
            />
          </CCol>
          <CCol xs="12" sm="12" md="6" lg="3" xl="2" xxl="2">
            <CInput
              value={watch("storePhone") || ""}
              readOnly
              label="Số điện thoại"
            />
          </CCol>
          <CCol
            xs="12"
            sm="12"
            md="12"
            lg="6"
            xl="4"
            xxl="5"
            className="btn-search"
          >
            <CCheckbox
              value={autoSuggest}
              disabled={!isUpdated || !watch("store_code")}
              onChange={toggleAutoSuggest}
              label="Hệ thống tự động đề xuất Nguyên Vật Liệu"
            />
          </CCol>
        </CRow>
      </CCollapse>
    </>
  );
  //#endregion
};
